<ng-template
  rt-connected-overlay
  #viewSettingsOverlay="rtConnectedOverlay"
  [rtConnectedOverlayAutoClose]="false"
  [hasBackdrop]="true"
  [origin]="settingsOrigin"
  (backdropClick)="onSettingsBackdropClick()"
>
  <rt-views-view-settings
    [dataSource]="dataSource"
    [viewForm]="viewForm"
    [layouts]="dataSource.layouts"
    [swimlanes]="dataSource.swimlanes"
    [groups]="dataSource.groups"
    [isViewCreate]="isViewCreate"
    [isViewPredefined]="!!currentViewClean()?.predefined"
    [openAt]="settingsOpenAt"
    [hasMoreSettings]="(dataSourceService.hasMoreSettings$ | async) ?? false"
    (delete)="onDeleteView()"
    (duplicate)="onDuplicateView()"
  />
</ng-template>

<ng-template
  rt-connected-overlay
  #viewSaveOverlay="rtConnectedOverlay"
  [hasBackdrop]="true"
  (backdropClick)="viewSaveOverlay.open = false"
>
  <rt-dropdown>
    <rt-dropdown-header>{{ 'app.views.view_page.save_view' | translate }}</rt-dropdown-header>
    <rt-dropdown-item (click)="saveView()">
      <rt-icon prefix name="save" />
      <rt-heading>{{ 'app.views.view_page.save_view' | translate }}</rt-heading>
      <div text-default text-small text-color>
        {{ 'app.views.view_page.save_view_description' | translate }}
      </div>
    </rt-dropdown-item>
    <rt-dropdown-item [routerLink]="['create']">
      <rt-icon prefix name="lock" />
      <rt-heading>{{ 'app.views.view_page.save_new_view' | translate }}</rt-heading>
      <div text-default text-small text-color>
        {{ 'app.views.view_page.save_new_view_description' | translate }}
      </div>
    </rt-dropdown-item>
  </rt-dropdown>
</ng-template>

<rtc-detached-router-outlet name="sidebar" />

<div
  #viewContainer
  class="data-view"
  [ngClass]="currentLayoutType ? ['data-view--' + currentLayoutType] : []"
  [rtResizeObserver]="{ initial: true }"
  (resized)="headerWidth.set($event.width)"
>
  <div
    class="header-container"
    #headerContainer
    [@toolbarCollapse]="toolbarState()"
    (@toolbarCollapse.start)="toolbarAnimationHandler.start()"
    (@toolbarCollapse.done)="toolbarAnimationHandler.end()"
    [style.width.px]="headerWidth()"
    [ngClass]="['header-container--' + toolbarState()]"
    [rtResizeObserver]="{ initial: true }"
    (resized)="onHeaderContainerResize($event, headerContainer, toolbarState())"
  >
    <div class="data-view--header" [@titleAnimation]="toolbarState()">
      <div layout layout-align-items-center>
        <rt-heading
          [variant]="!viewForm.value.name ? 'large-x' : 'heading light large-x dark'"
          margin-right-small
        >
          {{ viewForm.value.name || ('app.views.view_page.view_name_placeholder' | translate) }}
          @let currentViewCleanValue = currentViewClean();
          @if (
            currentViewCleanValue && (viewFormChanged || !!currentViewSnapshot()) && !isViewCreate
          ) {
            <div layout layout-align-self-start margin-left-small>
              @if (!currentViewCleanValue.predefined) {
                <rt-button
                  #saveOrigin="rtOverlayOrigin"
                  rt-overlay-origin
                  size="small"
                  variant="outline"
                  [dropdownOpened]="viewSaveOverlay.open"
                  (click)="onSaveViewDropdownClick(saveOrigin, viewSaveOverlay)"
                  margin-right-small-x
                >
                  {{ 'app.views.view_page.save_view' | translate }}
                </rt-button>
              } @else {
                <rt-button
                  size="small"
                  variant="outline"
                  [routerLink]="['create']"
                  margin-right-small-x
                >
                  {{ 'app.views.view_page.save_new_view' | translate }}
                </rt-button>
              }
              <rt-icon-button
                variant="outline"
                size="small"
                iconName="replay"
                [rtTooltip]="'app.views.view_page.reset_view' | translate"
                (click)="resetView()"
              />
            </div>
          }
        </rt-heading>
      </div>

      @if ((dataSourceService.viewPageHeaderComponent$ | async) ?? null; as headerComponent) {
        <div class="data-source-header-component-outlet">
          <ng-template [rtComponentOutlet]="headerComponent" />
        </div>
      }
      @if (dataSourceService.pageCtaVisible$ | async) {
        <rt-button
          [disabled]="isViewCreate"
          variant="primary"
          (click)="onPageCtaClick()"
          margin-left-small
        >
          <rt-icon name="add" />
          {{ dataSourceService.translations.pageCta | translate }}
        </rt-button>
      }
    </div>

    <rt-divider />

    @if (viewForm.value.layout!.type === 'table') {
      <rt-bulk-actions-selection-message [hasBottomMargin]="false" margin-lr />
    }
    <div
      class="data-view--filters"
      [@filtersAnimation]="toolbarState()"
      [class.disabled]="!pageReady()"
    >
      @if (bulkActionsSelectedItems.length) {
        <rt-bulk-actions-toolbar
          [buttonMode]="bulkActionsButtonMode[viewForm.value.layout!.type!]!"
        />
      } @else {
        <rt-views-quick-filters-container
          [viewsRecordsTotalCount]="viewsRecordsTotalCount"
          layout-align-self-center
        />
      }
      <div class="controls">
        <div margin-right-small-x>
          <ng-container
            [ngComponentOutlet]="searchComponent.component"
            [ngComponentOutletInputs]="searchComponent.inputs"
          />
        </div>
        <rt-group variant="stacked horizontal-manual">
          <rt-view-sort />
          <rt-icon-button
            [pressed]="viewSettingsOverlay.open && settingsOpenAt === 'groupingSelectTmpl'"
            [active]="!!viewForm.value.group?.configId"
            (click)="onGroupingClick()"
            variant="tertiary"
            iconName="auto-awesome-motion"
          />
          <rt-icon-button
            [pressed]="viewSettingsOverlay.open && settingsOpenAt === 'mainTemplate'"
            rt-overlay-origin
            #settingsOrigin="rtOverlayOrigin"
            variant="tertiary"
            iconName="more-horiz"
            class="is-last"
            (click)="viewSettingsOverlay.open = true"
          />
        </rt-group>
      </div>
    </div>
  </div>

  <div
    class="layout-outlet"
    [style.padding-top.px]="currentLayoutType !== 'board' ? headerHeight : null"
  >
    @if (hasSampleData && !isSampleDataInProgress) {
      <rt-views-sample-data-infobox
        class="infobox"
        [attr.margin-top-small-x]="toolbarState() === 'hidden' ? '' : null"
      />
    }

    @if ((dataSourceService.infoboxComponent$ | async) ?? null; as infoboxComponent) {
      <div class="infobox">
        <ng-template
          *rtComponentOutlet="
            infoboxComponent;
            inputs: {
              count: viewsRecordsTotalCount,
              jobsToUpdateCount: viewListCountersMap['jobs:prosemirror'] || 0,
            }
          "
        />
      </div>
    }
    @if (isEmpty && isLoaded) {
      <rt-views-empty-state
        [defaultEmptyState]="(dataSourceService.defaultEmptyState$ | async)!"
        [filteredEmptyState]="(dataSourceService.filteredEmptyState$ | async)!"
        [customEmptyState]="currentViewClean()?.emptyState ?? null"
        [isViewPredefined]="!!currentViewClean()?.predefined"
        [filtersChanged]="filtersChanged()"
        [filtersApplied]="filtersApplied()"
        class="data-view--empty-state"
      />
    } @else {
      <ng-container [ngComponentOutlet]="layoutContainerComponent" />
    }
  </div>
</div>
